import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authService } from "../services/authService";
import { setUserContext, clearUserContext } from "../config/sentry";
import i18n, { SUPPORTED_LOCALES, type SupportedLocale } from "../i18n";

/** Role value for admin (aligns with backend rbac.DELETE_ORDERS_PURCHASES_CUSTOMERS_ROLES). */
export const ROLE_ADMIN = "admin";

interface User {
  id: string;
  email: string | null;
  pseudoLogin?: string | null;
  fullName: string | null;
  role: string;
  locale?: string; // Story 11.1: i18n locale preference
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // 2FA state (Story 1.7, 6-6-9)
  pendingTwoFactor: boolean;
  twoFactorSessionToken: string | null;
  gracePeriodStarted: boolean;
  graceDaysRemaining: number | null;
  totpDisabledByAdmin: boolean; // Story 6-6-9: show admin recovery banner

  // Actions
  login: (token: string, user: User) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  checkAuth: () => Promise<void>;

  // 2FA actions
  setPendingTwoFactor: (sessionToken: string) => void;
  completeTwoFactor: (token: string) => Promise<void>;
  clearPendingTwoFactor: () => void;
  setGracePeriod: (started: boolean, daysRemaining: number | null) => void;
  setTotpDisabledByAdmin: (value: boolean) => void;
}

function applyUserLocale(user: User | null): void {
  if (!user?.locale) {
    return;
  }

  const normalizedLocale = user.locale.slice(0, 2).toLowerCase() as SupportedLocale;
  if (!SUPPORTED_LOCALES.includes(normalizedLocale)) {
    return;
  }

  void i18n.changeLanguage(normalizedLocale);
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      // 2FA state
      pendingTwoFactor: false,
      twoFactorSessionToken: null,
      gracePeriodStarted: false,
      graceDaysRemaining: null,
      totpDisabledByAdmin: false,

      login: (token: string, user: User) => {
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
          pendingTwoFactor: false,
          twoFactorSessionToken: null,
        });

        applyUserLocale(user);
        
        // Set Sentry user context for error tracking
        setUserContext(
          user.id,
          user.email ?? (user.pseudoLogin ? `pseudo:${user.pseudoLogin}` : ""),
          user.role
        );
      },

      logout: () => {
        // Clear Sentry user context
        clearUserContext();
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          pendingTwoFactor: false,
          twoFactorSessionToken: null,
          gracePeriodStarted: false,
          graceDaysRemaining: null,
          totpDisabledByAdmin: false,
        });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
        applyUserLocale(user);
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      checkAuth: async () => {
        const token = get().token;

        if (!token) {
          set({ isAuthenticated: false, user: null, isLoading: false });
          clearUserContext(); // Clear Sentry context if no token
          return;
        }

        try {
          const { user, apiUser } = await authService.getCurrentUser();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            gracePeriodStarted: apiUser.totp_grace_started ?? false,
            graceDaysRemaining: apiUser.totp_grace_days_remaining ?? null,
            totpDisabledByAdmin: apiUser.totp_disabled_by_admin ?? false,
          });

          applyUserLocale(user);

          // Set Sentry user context
          setUserContext(
          user.id,
          user.email ?? (user.pseudoLogin ? `pseudo:${user.pseudoLogin}` : ""),
          user.role
        );
        } catch {
          // Token invalid/expired
          clearUserContext(); // Clear Sentry context on error
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          localStorage.removeItem("auth-storage");
        }
      },

      // 2FA actions
      setPendingTwoFactor: (sessionToken: string) => {
        set({
          pendingTwoFactor: true,
          twoFactorSessionToken: sessionToken,
          isLoading: false,
        });
      },

      completeTwoFactor: async (token: string) => {
        // Store token temporarily to make authenticated request
        const tempStorage = { state: { token } };
        localStorage.setItem("auth-storage", JSON.stringify(tempStorage));

        // Get user info after 2FA
        const { user, apiUser } = await authService.getCurrentUser();
        set({
          token,
          user,
          isAuthenticated: true,
          isLoading: false,
          pendingTwoFactor: false,
          twoFactorSessionToken: null,
          gracePeriodStarted: apiUser.totp_grace_started ?? false,
          graceDaysRemaining: apiUser.totp_grace_days_remaining ?? null,
          totpDisabledByAdmin: apiUser.totp_disabled_by_admin ?? false,
        });

        applyUserLocale(user);
        
        // Set Sentry user context after 2FA
        setUserContext(
          user.id,
          user.email ?? (user.pseudoLogin ? `pseudo:${user.pseudoLogin}` : ""),
          user.role
        );
      },

      clearPendingTwoFactor: () => {
        set({
          pendingTwoFactor: false,
          twoFactorSessionToken: null,
        });
      },

      setGracePeriod: (started: boolean, daysRemaining: number | null) => {
        set({
          gracePeriodStarted: started,
          graceDaysRemaining: daysRemaining,
        });
      },

      setTotpDisabledByAdmin: (value: boolean) => {
        set({ totpDisabledByAdmin: value });
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        gracePeriodStarted: state.gracePeriodStarted,
        graceDaysRemaining: state.graceDaysRemaining,
        totpDisabledByAdmin: state.totpDisabledByAdmin,
      }),
    }
  )
);
